import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LUXURY_TIER_LABELS,
  LUXURY_TIERS,
  SALE_CATEGORIES,
  type BrandSummary,
  type LuxuryTier,
  type SaleCategory,
  type SaleStatus,
  type UpdateBrandInput,
} from '../../shared/saleFinder';
import {
  addBrand,
  deleteBrand,
  getBrands,
  scanBrand,
  setBrandEnabled,
  updateBrand,
} from '../lib/saleFinder';
import { Icon } from './Icon';

const STATUS_SHORT: Record<SaleStatus, string> = {
  LIVE: 'Live',
  NO_ACTIVE_SALE: 'No sale',
  OUT_OF_STOCK: 'Sold out',
  EXPIRED: 'Expired',
  UNVERIFIED: 'Unverified',
  SCAN_FAILED: 'Failed',
};

function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

function BrandRow({
  brand,
  busy,
  scanning,
  onToggle,
  onScan,
  onEdit,
  onDelete,
}: {
  brand: BrandSummary;
  busy: boolean;
  scanning: boolean;
  onToggle: (brand: BrandSummary) => void;
  onScan: (brand: BrandSummary) => void;
  onEdit: (brand: BrandSummary, patch: UpdateBrandInput) => void;
  onDelete: (brand: BrandSummary) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(brand.name);
  const [tier, setTier] = useState<LuxuryTier>(brand.luxuryTier);
  const [category, setCategory] = useState<SaleCategory>(brand.categories[0] ?? 'handbags');

  if (editing) {
    return (
      <div className="brand-row brand-row--editing">
        <div className="brand-add__grid">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} aria-label="Brand name" />
          <select value={tier} onChange={(e) => setTier(e.target.value as LuxuryTier)} aria-label="Luxury tier">
            {LUXURY_TIERS.map((t) => (
              <option key={t} value={t}>
                {LUXURY_TIER_LABELS[t]}
              </option>
            ))}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value as SaleCategory)} aria-label="Category">
            {SALE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="brand-row__actions">
          <button
            type="button"
            onClick={() => {
              onEdit(brand, { name, luxuryTier: tier, categories: [category] });
              setEditing(false);
            }}
            disabled={busy || !name.trim()}
          >
            Save
          </button>
          <button type="button" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`brand-row${brand.enabled ? '' : ' brand-row--off'}`}>
      <div className="brand-row__main">
        <strong>{brand.name}</strong>
        <small>
          {LUXURY_TIER_LABELS[brand.luxuryTier]}
          {brand.latestStatus ? ` · ${STATUS_SHORT[brand.latestStatus]}` : ''}
        </small>
      </div>
      <div className="brand-row__actions">
        <button type="button" onClick={() => void onScan(brand)} disabled={busy || !brand.enabled}>
          {scanning ? (
            <>
              <Spinner /> Scanning…
            </>
          ) : (
            'Scan'
          )}
        </button>
        <button type="button" onClick={() => setEditing(true)} disabled={busy}>
          Edit
        </button>
        <button type="button" onClick={() => void onToggle(brand)} disabled={busy}>
          {brand.enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          type="button"
          className="brand-row__delete"
          onClick={() => void onDelete(brand)}
          disabled={busy}
          aria-label={`Delete ${brand.name}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export function BrandsPage() {
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [scanningId, setScanningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [tier, setTier] = useState<LuxuryTier>('accessible_premium');
  const [category, setCategory] = useState<SaleCategory>('handbags');
  const [domain, setDomain] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBrands(await getBrands());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Brands could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggle = async (brand: BrandSummary) => {
    setBusyId(brand.id);
    setError(null);
    try {
      await setBrandEnabled(brand.id, !brand.enabled);
      setBrands((current) =>
        current.map((b) => (b.id === brand.id ? { ...b, enabled: !b.enabled } : b)),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That change could not be saved.');
    } finally {
      setBusyId(null);
    }
  };

  const handleScan = async (brand: BrandSummary) => {
    setBusyId(brand.id);
    setScanningId(brand.id);
    setError(null);
    setMessage(null);
    try {
      const result = await scanBrand(brand.id, true);
      setMessage(
        result
          ? `${brand.name}: ${result.liveFound} live · ${result.candidatesChecked} page${result.candidatesChecked === 1 ? '' : 's'} checked.`
          : `${brand.name} scanned.`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The scan could not run.');
    } finally {
      setBusyId(null);
      setScanningId(null);
    }
  };

  const handleEdit = async (brand: BrandSummary, patch: UpdateBrandInput) => {
    setBusyId(brand.id);
    setError(null);
    setMessage(null);
    try {
      await updateBrand(brand.id, patch);
      setMessage(`${patch.name ?? brand.name} updated.`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That change could not be saved.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (brand: BrandSummary) => {
    if (!window.confirm(`Delete ${brand.name}? This removes its sales and domains too.`)) return;
    setBusyId(brand.id);
    setError(null);
    setMessage(null);
    try {
      await deleteBrand(brand.id);
      setBrands((current) => current.filter((b) => b.id !== brand.id));
      setMessage(`${brand.name} deleted.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That brand could not be deleted.');
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Enter a brand name.');
      return;
    }
    setAdding(true);
    setError(null);
    setMessage(null);
    try {
      await addBrand({
        name: name.trim(),
        luxuryTier: tier,
        categories: [category],
        officialDomain: domain.trim() || null,
      });
      setMessage(`${name.trim()} added.`);
      setName('');
      setDomain('');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That brand could not be added.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <section className="brands-page">
      <header className="sales-page__head">
        <div>
          <h1>Brands</h1>
          <p>{brands.length} in the registry</p>
        </div>
        <NavLink className="sales-page__brands-link" to="/sales">
          Back to sales
        </NavLink>
      </header>

      {message && <p className="sales-page__message">{message}</p>}
      {error && <p className="sales-page__error">{error}</p>}

      <form className="brand-add" onSubmit={(e) => void handleAdd(e)}>
        <h2>Add a brand</h2>
        <div className="brand-add__grid">
          <input
            type="text"
            placeholder="Brand name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Brand name"
          />
          <input
            type="text"
            placeholder="Official website (optional)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            aria-label="Official website"
          />
          <select value={tier} onChange={(e) => setTier(e.target.value as LuxuryTier)} aria-label="Luxury tier">
            {LUXURY_TIERS.map((t) => (
              <option key={t} value={t}>
                {LUXURY_TIER_LABELS[t]}
              </option>
            ))}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value as SaleCategory)} aria-label="Category">
            {SALE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <button className="primary-button" type="submit" disabled={adding}>
          <Icon name="add" size={16} />
          {adding ? 'Adding…' : 'Add brand'}
        </button>
      </form>

      {loading ? (
        <p className="sales-page__empty">Loading brands…</p>
      ) : (
        <div className="brand-list">
          {brands.map((brand) => (
            <BrandRow
              key={brand.id}
              brand={brand}
              busy={busyId === brand.id}
              scanning={scanningId === brand.id}
              onToggle={(target) => void handleToggle(target)}
              onScan={(target) => void handleScan(target)}
              onEdit={(target, patch) => void handleEdit(target, patch)}
              onDelete={(target) => void handleDelete(target)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
